import type { BusinessInformationStepSchema } from "Components/Onboarding/BusinessEntity/Steps/BusinessInformationStep/BusinessInformationStepContent";
import type { PartyWithSingleAddress } from "Components/Onboarding/types";
import {
  type AddressDto,
  type AddressType,
  type PartyDto,
  PartyType,
} from "Infrastructure/Api/Api";

export const getPartyWithSingleAddress = (
  party: PartyDto,
  addressType: AddressType,
): PartyWithSingleAddress => ({
  ...party,
  address: party.addresses.find(({ type }) => type === addressType) || {
    type: addressType,
  },
});

// region and vatRegistryStatus are not derivable from the party — the caller supplies them (region from
// the persisted IdentityUser region or the inline selector, the register status from the VAT lookup), so
// both are excluded from these defaults.
export const getBusinessInformationStepDefaultValues = (
  party: PartyDto,
  addressType: AddressType,
): Omit<BusinessInformationStepSchema, "region" | "vatRegistryStatus"> => ({
  ...party,
  publicID: party.publicID ?? "",
  phone: party.phone,
  email: party.email ?? "",
  companyName: party.companyName ?? "",
  companyNumber: party.companyNumber ?? "",
  address: party.addresses.find(({ type }) => type === addressType) || {
    type: addressType,
  },
});

export const upsertAddressByType = (
  existingAddresses: AddressDto[],
  updatedAddress: AddressDto,
): AddressDto[] => {
  const hasExisting = existingAddresses.some(
    (a) => a.type === updatedAddress.type,
  );

  if (hasExisting) {
    return existingAddresses.map((a) =>
      a.type === updatedAddress.type
        ? {
            ...updatedAddress,
            addressID: a.addressID ?? updatedAddress.addressID,
          }
        : a,
    );
  }

  return [...existingAddresses, updatedAddress];
};

export const getCompanyName = ({
  type,
  companyName,
  firstName,
  lastName,
}: PartyDto) => {
  if (
    type === PartyType.SelfEmployed &&
    !companyName &&
    (firstName || lastName)
  ) {
    return [firstName, lastName].join(" ");
  }

  return companyName ?? undefined;
};
